import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppWindow } from 'lucide-react';
import { listSoftware } from '@/api/software';
import { useAuth } from '@/auth/useAuth';
import { useSort, sortRows, matchesQuery } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';
import type { SoftwarePool } from '@/types/oscar';

export function SoftwareRegister() {
  const { canMutate } = useAuth();
  const [search, setSearch] = useState('');
  const [licenceType, setLicenceType] = useState('');
  const { sortKey, sortDir, onSort } = useSort('title');
  const query = useQuery({ queryKey: ['software'], queryFn: ({ signal }) => listSoftware(signal) });

  const colValue = (p: SoftwarePool, k: string): unknown =>
    ({ title: p.Title, vendor: p.Vendor, sku: p.SKU, licenceType: p.LicenceType, seats: p.TotalSeats } as Record<string, unknown>)[k];
  const licenceTypeOpts = [...new Set((query.data ?? []).map((p) => p.LicenceType).filter(Boolean))].sort();
  const rows = clusterByLink(sortRows(
    (query.data ?? []).filter((p) => matchesQuery(search, [p.Title, p.Vendor, p.SKU]) && (!licenceType || p.LicenceType === licenceType)),
    (p) => colValue(p, sortKey),
    sortDir,
  ));

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Software</h1>
          <p className="mt-1 text-sm text-darkGrey/80">Licence pools and seat utilisation.</p>
        </div>
        {canMutate && (
          <Link to="/software/new" className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
            <AppWindow className="h-4 w-4" aria-hidden /> Add pool
          </Link>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Licence type
          <select aria-label="Filter by licence type" value={licenceType} onChange={(e) => setLicenceType(e.target.value)} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {licenceTypeOpts.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input aria-label="Search software" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title, vendor or SKU" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
      </div>

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <SortHeader label="Title" columnKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Vendor" columnKey="vendor" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="SKU (text)" columnKey="sku" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2">SKU (ID)</th>
              <SortHeader label="Type" columnKey="licenceType" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Utilisation" columnKey="seats" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="w-64" />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={6} className="px-3 py-6 text-center text-red">Could not load software.</td></tr>}
            {rows.length === 0 && !query.isLoading && <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">No licence pools yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.SoftwarePoolId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2">
                  <Link to={`/software/${p.SoftwarePoolId}`} className="font-medium text-navy hover:underline">{p.Title}</Link>
                  {p.LinkGroupId && <span className="ml-2 rounded-full bg-lightBlue px-1.5 py-0.5 text-[10px] font-semibold text-navy" title="Linked to other pools — allocations mirror">Linked</span>}
                </td>
                <td className="px-3 py-2 text-darkGrey/80">{p.Vendor ?? '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{p.SKU ?? '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{p.SkuId ?? '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{p.LicenceType}</td>
                <td className="px-3 py-2"><UtilisationBar pool={p} breakdown /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): licence pools as stacked cards. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load software.</p>}
        {rows.length === 0 && !query.isLoading && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No licence pools yet.</p>
        )}
        {rows.map((p) => (
          <MobileCard key={p.SoftwarePoolId}>
            <div className="flex items-start justify-between gap-2">
              <Link to={`/software/${p.SoftwarePoolId}`} className="text-sm font-semibold text-navy hover:text-orange hover:underline">
                {p.Title}
              </Link>
              {p.LinkGroupId && (
                <span className="shrink-0 rounded-full bg-lightBlue px-1.5 py-0.5 text-[10px] font-semibold text-navy" title="Linked to other pools — allocations mirror">Linked</span>
              )}
            </div>
            <div className="mt-2 border-t border-border pt-2">
              <CardField label="Vendor">{p.Vendor ?? '—'}</CardField>
              <CardField label="SKU (text)">{p.SKU ?? '—'}</CardField>
              <CardField label="SKU (ID)">{p.SkuId ?? '—'}</CardField>
              <CardField label="Type">{p.LicenceType}</CardField>
            </div>
            <div className="mt-2">
              <div className="mb-1 text-[0.62rem] font-bold uppercase tracking-wider text-darkGrey/45">Utilisation</div>
              <UtilisationBar pool={p} breakdown />
            </div>
          </MobileCard>
        ))}
      </MobileCards>
    </section>
  );
}

// Keep linked-group members adjacent: anchor each group at its first-sorted
// member and pull its siblings in right after, preserving the sort otherwise.
function clusterByLink(rows: SoftwarePool[]): SoftwarePool[] {
  const out: SoftwarePool[] = [];
  const seen = new Set<number>();
  for (const p of rows) {
    if (seen.has(p.SoftwarePoolId)) continue;
    out.push(p);
    seen.add(p.SoftwarePoolId);
    if (p.LinkGroupId) {
      for (const q of rows) {
        if (!seen.has(q.SoftwarePoolId) && q.LinkGroupId === p.LinkGroupId) {
          out.push(q);
          seen.add(q.SoftwarePoolId);
        }
      }
    }
  }
  return out;
}

export function UtilisationBar({ pool, breakdown = false }: { pool: SoftwarePool; breakdown?: boolean }) {
  const total = pool.TotalSeats;
  // Scale the segments so they always fit: against total when within seats, or
  // against used when the pool is over its nominal seats (so the split stays
  // truthful and the bar simply reads full).
  const denom = Math.max(total ?? 0, pool.SeatsUsed, 1);
  const w = (n: number) => `${Math.min(100, (n / denom) * 100)}%`;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-darkGrey/80">
        <span>{pool.SeatsUsed}{total != null ? ` / ${total}` : ' (unlimited)'}</span>
        {pool.OverAllocated && <span className="font-semibold text-red">Over-allocated</span>}
      </div>
      {total != null && (
        // White track + border so the unused (remaining) portion is clearly
        // visible. Mandatory = navy (dedicated), Optional = orange (reclaimable),
        // Unclassified = grey. Over-allocation (Mandatory > total) turns it red.
        <div className={`mt-1 flex h-2.5 w-full overflow-hidden rounded border bg-white ${pool.OverAllocated ? 'border-red/70 ring-1 ring-red/40' : 'border-darkGrey/25'}`}>
          <div className="h-full bg-navy" style={{ width: w(pool.MandatorySeats) }} title={`Mandatory ${pool.MandatorySeats}`} />
          <div className="h-full bg-orange" style={{ width: w(pool.OptionalSeats) }} title={`Optional ${pool.OptionalSeats}`} />
          <div className="h-full bg-darkGrey/40" style={{ width: w(pool.UnclassifiedSeats) }} title={`Unclassified ${pool.UnclassifiedSeats}`} />
        </div>
      )}
      {breakdown && total != null && (
        <div className="mt-1 text-[11px] text-darkGrey/60">
          {pool.MandatorySeats} mandatory · {pool.OptionalSeats} optional{pool.UnclassifiedSeats > 0 ? ` · ${pool.UnclassifiedSeats} other` : ''}
        </div>
      )}
    </div>
  );
}
